<template>
    <div class="notifications">
        <b-dropdown
            id="notification-dropdown"
            variant="primary"
            toggle-class="notification-dropdown"
            menu-class="notification-dropdown-nav"
            no-caret
            right
            boundary="viewport">
            <template #button-content>
                <i class="fad fa-fw fa-bell"></i>
                <span
                    class="count-bubble"
                    v-if="unReadNotifications >= 1"
                    >{{ unReadNotifications }}</span
                >
            </template>

            <b-dropdown-text
                v-if="notifications.length === 0"
                tag="div"
                text-class="notification-item empty-notification-list">
                {{ $t('notification.no_notifications') }}.
            </b-dropdown-text>

            <b-dropdown-item
                v-for="notification in notifications"
                v-else
                :href="`/notification/${notification.id}`"
                :key="notification.id"
                :link-class="notification.read_at === null ? 'new notification-item' : 'notification-item'">
                <div :class="`notification-icon ${notification.web_icon}`"></div>
                <div class="content">
                    <div class="title-text">{{ notification.title }}</div>
                    <div class="date">{{ notification.created_at | date_time_om }}</div>
                    <div class="description">
                        {{ notification.description }}
                    </div>
                </div>

                <div class="visibility-toggle">
                    <i
                        class="fad fa-eye-slash visibility-icon"
                        v-if="notification.read_at === null"
                        @click.prevent.stop="markAsRead(notification.id)">
                    </i>
                    <i
                        class="fad fa-eye visibility-icon"
                        v-else></i>
                </div>
            </b-dropdown-item>

            <b-dropdown-item-button
                button-class="btn-show-more btn-show-more-notifications"
                @click="showAllNotifications">
                {{ notifications.length > 5 ? $t('common.more') : $t('common.manage') }}
            </b-dropdown-item-button>
        </b-dropdown>

        <b-tooltip
            target="notification-dropdown"
            :title="$t('notification.notifications')"></b-tooltip>
    </div>
</template>

<script>
export default {
    name: 'design-2021-notification-component',
    props: {
        notificationsIn: {
            type: Array,
            required: false,
            default: () => [],
        },
    },
    data() {
        return {
            notifications: this.notificationsIn,
        };
    },
    computed: {
        unReadNotifications() {
            if (this.notifications.length === 0) {
                return null;
            }

            const unread = this.notifications.filter((notification) => notification.read_at === null);

            return unread.length;
        },
    },

    methods: {
        markAsRead(id) {
            this.axios.post(this.routes.notificationRead(id)).then(
                (response) => {
                    this.notifications = this.notifications.map((notification) => {
                        if (notification.id === response.data.id) {
                            notification = response.data;
                        }
                        return notification;
                    });
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        showAllNotifications() {
            window.location.href = this.routes.notifications();
        },
    },
};
</script>

<style scoped lang="scss">
.notifications ::v-deep {
    align-self: baseline;

    .notification-dropdown {
        padding: 0;
        border-radius: $border-radius-content;
        color: $white;
        background-color: $primary-darken-alternative;
        border: none;
        height: 35px;
        width: 35px;

        i {
            font-size: 1.25rem;
            color: $white;

            &:before {
                opacity: 0.4;
            }

            &:after {
                opacity: 1;
            }
        }

        &:focus,
        &:active,
        &:not(:disabled):not(.disabled):active:focus,
        &:not(:disabled):not(.disabled):active {
            box-shadow: none;
            color: $white;
            background-color: darken($primary-darken-alternative, 10%);
        }

        &:hover {
            color: $white !important;
            background-color: darken($primary-darken-alternative, 10%);
        }
    }

    .notification-dropdown-nav {
        width: 250px;
        max-height: 100%;
        overflow-y: auto;
        border-radius: $border-radius-content;
        padding: map-get($grid-gutter-spacing, 'fifteen');

        li {
            margin-bottom: map-get($grid-gutter-spacing, 'five');
            padding-bottom: map-get($grid-gutter-spacing, 'five');

            &:not(:last-child) {
                border-bottom: 2px dotted $border-color;
            }
        }

        .btn-show-more-notifications {
            display: block;
            text-align: right;
            margin: map-get($grid-gutter-spacing, 'eight') 0;
            font-weight: $font-weight-bold;
        }
    }

    .count-bubble {
        display: flex;
        justify-content: center;
        align-items: center;
        position: absolute;
        background: $danger;
        color: $white;
        font-size: 0.5625rem;
        border-radius: $border-radius-content;
        width: 20px;
        height: 20px;
        top: -10px;
        right: -2px;
        font-family: $headings-font-family;
    }

    .notification-item {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-start;
        justify-content: flex-start;
        font-size: $default-font-size;
        padding: map-get($grid-gutter-spacing, 'ten');

        .notification-icon {
            font-size: $base-font-size;
            color: $text-color;
            margin-right: map-get($grid-gutter-spacing, 'eight');
            align-self: flex-start;

            &:before {
                margin-right: 0;
            }
        }

        .content {
            margin-right: 0;
            color: $text-color;
            line-height: 1;
            width: calc(100% - 60px);
            align-self: flex-start;
            white-space: normal;
            flex-grow: 1;

            .title-text {
                font-weight: $font-weight-bold;
            }

            .date {
                font-size: 0.75rem;
                color: $text-muted-color;
                margin-top: map-get($grid-gutter-spacing, 'five');
                font-weight: $font-weight-normal;
            }

            .description {
                font-size: 0.75rem;
                margin-top: map-get($grid-gutter-spacing, 'five');
                font-weight: $font-weight-normal;
            }
        }

        .visibility-toggle {
            flex: 1 0 100%;
            text-align: right;
            position: relative;
            z-index: 10;

            i {
                color: $text-color;
                font-size: $default-font-size;
                margin-left: auto;
            }
        }

        &:hover {
            border-radius: $border-radius-content;
            background-color: $secondary-background-color;
            cursor: pointer;
        }

        &.empty-notification-list {
            &,
            &:hover {
                color: $text-muted-color;
                font-weight: $font-weight-medium;
                background-color: transparent;
                cursor: default;
            }
        }

        &:not(.new) {
            .title {
                color: $text-color;
                opacity: 0.5;
            }
        }

        &.new {
            .notification-icon,
            .content .title-text,
            .visibility-toggle i {
                color: var(--primary);
            }

            &:hover {
                .visibility-toggle:hover i {
                    color: var(--primaryDarker);
                }
            }
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'sm')) {
    .notifications ::v-deep {
        .notification-dropdown-nav {
            max-height: 470px;
        }
    }
}
</style>
